// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Plural, Trans } from '@lingui/macro';
import { Form, Formik } from 'formik';
import React from 'react';
import { Box, Text } from '~/design-system';
import ImportOptionsSkeleton from '../ImportOptionsSkeleton';
import ReadyCheckboxField from '../ReadyCheckboxField';

/**
 * Renders the ready to import page with the import statistics
 * The view. The props are passed in from useReady - all the logic resides there.
 * @param {{
 *    setFormikContext: ReturnType<import('../../useFormikOrchestration').default>["setFormikContext"]
 * } & ReturnType<useReady>} props
 * @returns
 */
export default function ReadyToImport({
  importData,
  loading,
  setFormikContext,
  onSubmit,
}) {
  if (loading)
    return (
      <Box>
        <ImportOptionsSkeleton />
      </Box>
    );
  const { preview: previewStatistics } = importData || {};
  return (
    <Box>
      <ImportOptions
        statistics={previewStatistics}
        setFormikContext={setFormikContext}
        onSubmit={onSubmit}
      />
    </Box>
  );
}

function ImportOptions({ statistics, setFormikContext, onSubmit }) {
  const productStatistics = statistics?.find(
    (stat) => stat.object === 'PRODUCT'
  );
  const initialValues = {
    updated: productStatistics?.updatedCount > 0,
    unchanged: productStatistics?.unchangedCount > 0,
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(props) => {
        // Map to import-specific flags
        const importOptions = {
          updateProducts: props.updated,
        };
        onSubmit(importOptions);
      }}
    >
      {(formikContext) => {
        setFormikContext(formikContext);
        return (
          <Form>
            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Text sx={{ marginTop: 3 }}>
                  <Trans>All your data is valid and ready to import.</Trans>
                </Text>
              </Box>
              <Box sx={{ margin: 'auto', width: '330px', textAlign: 'left' }}>
                <ReadyCheckboxField
                  name="updated"
                  count={productStatistics?.updatedCount}
                  disabled={!initialValues.updated}
                >
                  <Plural
                    value={productStatistics?.updatedCount}
                    one={'Product that will be updated'}
                    other={'Products that will be updated'}
                  />
                </ReadyCheckboxField>
                <ReadyCheckboxField
                  name="unchanged"
                  disabled
                  count={productStatistics?.unchangedCount}
                >
                  <Plural
                    value={productStatistics?.unchangedCount}
                    one={'Product have no change'}
                    other={'Products have no change'}
                  />
                </ReadyCheckboxField>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 */
export function useReady({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [importExternalIds] = useMutation(IMPORT_EXTERNAL_IDS);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
    isSubmitting,
    onSubmit: async (importOptions) => {
      setIsSubmitting(true);
      const variables = {
        importId,
        importOptions,
      };
      await importExternalIds({ variables });
      setIsSubmitting(false);
    },
  };
}

/** @param {import('./ReadyToImport.generated').ProductDataReadyPageQuery | undefined} data */
function afterQuery(data) {
  if (!data) return;
  return {
    importData: data?.workspace?.dataImport,
    stats: data?.workspace?.dataImport?.preview,
  };
}

/** @type {typeof import('./ReadyToImport.generated').ProductDataReadyPageDocument} */
const QUERY = gql`
  query ProductDataReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        importOptions
        preview {
          object
          updatedCount
          unchangedCount
        }
      }
    }
  }
`;

/** @type {typeof import('./ReadyToImport.generated').FinalizeProductExternalIdImportDocument} */
export const IMPORT_EXTERNAL_IDS = gql`
  mutation finalizeProductDataImport(
    $importId: ID!
    $importOptions: ProductDataImportOptionsInput!
  ) {
    result: finalizeProductDataImport(
      input: { importId: $importId, importOptions: $importOptions }
    ) {
      code
      success
      message
    }
  }
`;
